import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Sparkles, ArrowLeft, Mail } from 'lucide-react';
import api from '../lib/api';
import useWin98Scope from '../hooks/useWin98Scope';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [smtpConfigured, setSmtpConfigured] = useState(null);
  useWin98Scope();

  const checkSmtp = useCallback(async () => {
    try {
      const response = await api.get('/settings/smtp-configured');
      setSmtpConfigured(response.data.configured);
    } catch (error) {
      setSmtpConfigured(false);
    }
  }, []);

  useEffect(() => {
    checkSmtp();
  }, [checkSmtp]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/auth/forgot-password', { email });
      setSent(true);
      toast.success('Reset link sent! Check your email.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset link');
    } finally {
      setLoading(false);
    }
  };

  if (smtpConfigured === null) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <p className="text-white">Loading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Brand mark on the desktop */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-3">
            <div className="w-12 h-12 bg-[var(--win-titlebar-start)] flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white">KyberCosplay</h1>
          </div>
        </div>

        <div className="win-window">
          <div className="win-titlebar">
            <span>Forgot Password</span>
          </div>
          <div className="p-6">
            {!smtpConfigured ? (
              <div className="text-center">
                <div className="win-well w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-[var(--win-titlebar-start)]" />
                </div>
                <h2 className="text-xl font-semibold text-black mb-2">
                  Password Reset Unavailable
                </h2>
                <p className="text-black mb-6">
                  Email service is not configured. Please contact the administrator to reset your password.
                </p>
                <Link to="/login">
                  <Button
                    variant="ghost"
                    className="win-btn-default"
                    data-testid="back-to-login-btn"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Login
                  </Button>
                </Link>
              </div>
            ) : sent ? (
              <div className="text-center">
                <div className="win-well w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-[var(--win-titlebar-start)]" />
                </div>
                <h2 className="text-xl font-semibold text-black mb-2">
                  Check Your Email
                </h2>
                <p className="text-black mb-6">
                  If an account exists with that email, we've sent password reset instructions.
                </p>
                <Link to="/login">
                  <Button
                    variant="ghost"
                    className="win-btn-default"
                    data-testid="back-to-login-btn"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Login
                  </Button>
                </Link>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <h2 className="text-xl font-semibold text-black mb-2">
                    Forgot Password?
                  </h2>
                  <p className="text-black">
                    Enter your email and we'll send you a reset link.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-black">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="forgot-email-input"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      required
                      className="h-12"
                    />
                  </div>

                  <Button
                    type="submit"
                    data-testid="forgot-submit-btn"
                    disabled={loading}
                    className="w-full h-12 win-btn-accent win-btn-default font-semibold"
                  >
                    {loading ? 'Sending…' : 'Send Reset Link'}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <Link
                    to="/login"
                    className="text-[var(--win-titlebar-start)] underline text-sm font-medium inline-flex items-center gap-2"
                    data-testid="back-to-login-link"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Login
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
